import { Form } from 'antd';
import authApi from 'api/auth';
import { useAppTranslation } from 'app/hooks';
import VerticalForm from 'components/Form/VerticalForm';
import { showSuccessModal } from 'components/Modals/CommonModals';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { ChangePasswordFormRequestModel } from 'models/auth';
import React from 'react';
import { AiOutlineCloseCircle } from 'react-icons/ai';
import { FaLock } from 'react-icons/fa';
import FieldError from 'components/FormField/FieldError';
import PasswordField from 'components/FormField/PasswordField';

interface ChangePasswordPageProps {
  isShow: boolean;
  closePage: () => void;
}

const ChangePasswordPage: React.FunctionComponent<ChangePasswordPageProps> = ({
  isShow,
  closePage,
}) => {
  const [form] = Form.useForm();
  const t = useAppTranslation();
  const handleResponseError = useHandleResponseError();

  const onClosePageClick = () => {
    form.resetFields();
    closePage();
  };

  const onSubmit = async (value: ChangePasswordFormRequestModel) => {
    const { confirmPassword, ...rest } = value;
    const { ok, error } = await authApi.changePassword(rest);
    if (ok) {
      const onOk = () => {
        onClosePageClick();
      };
      showSuccessModal({
        title: t('changePassword.success.title'),
        content: t('changePassword.success.message'),
        onOk,
      });
    }
    if (error) {
      handleResponseError(error);
    }
  };

  return (
    <div className={`overlay center-content-col primary-text-color ${isShow ? 'show' : 'hidden'}`}>
      <div className="auth-form">
        <div className="auth-form__header">
          <h2> {t('changePassword.title')}</h2>
        </div>
        <button className="btn-close" onClick={onClosePageClick} type="button">
          <AiOutlineCloseCircle />
        </button>
        <VerticalForm
          className="auth__change-password"
          name="changePassword"
          form={form}
          onFinish={onSubmit}
        >
          <PasswordField
            name="oldPassword"
            placeholder={t('placeholder.enterOldPassword')}
            prefix={<FaLock />}
            className="input_primary"
            rules={[
              {
                required: true,
                message: <FieldError message={t('errors.password.required')} />,
              },
            ]}
          />
          <PasswordField
            name="newPassword"
            placeholder={t('placeholder.enterNewPassword')}
            prefix={<FaLock />}
            className="input_primary"
            rules={[
              {
                required: true,
                message: <FieldError message={t('errors.password.required')} />,
              },
              {
                min: 8,
                message: <FieldError message={t('errors.password.invalid')} />,
              },
              {
                max: 50,
                message: <FieldError message={t('errors.password.invalid')} />,
              },
            ]}
          />
          <PasswordField
            name="confirmPassword"
            placeholder={t('placeholder.confirmPassword')}
            prefix={<FaLock />}
            className="input_primary"
            rules={[
              {
                required: true,
                message: <FieldError message={t('changePassword.form.confirmPasswordRequired')} />,
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    <FieldError message={t('changePassword.form.passwordNotMatch')} />,
                  );
                },
              }),
            ]}
          />
          <div className="login__footer">
            <button type="submit" className="btn-submit">
              {t('changePassword.title')}
            </button>
          </div>
        </VerticalForm>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
